import {
  debounce,
  isDesktopBreakpoint,
  onDocumentReady,
} from "@theme/utilities";
import { ThemeEvents } from "@theme/events";

(() => {
  "use strict";

  if (window.__customHeaderMenuClickToggleInitialized) return;
  window.__customHeaderMenuClickToggleInitialized = true;

  /* ═══════════════════════════════════════════════════════════════════════════
     CUSTOM MEGA MENU IMAGES
     Adds promotional images and banners to mega menu dropdowns
     ═══════════════════════════════════════════════════════════════════════════ */

  /* ═══════════════════════════════════════════════════════════════════════════
     CONFIG
     ═══════════════════════════════════════════════════════════════════════════ */
  const CONFIG = {
    resizeDebounce: 250,
    selectors: {
      headerSection: ".header-section",
      imageColumn: ".custom-megamenu-image-column",
      image: ".custom-megamenu-image",
      banner: ".custom-megamenu-promo-banner",
      megaMenuSection: ".mega-menu.section",
      headerComponent: "#header-component",
      expandedMenuTrigger: '.menu-list__link[aria-expanded="true"]',
      submenu: '[ref="submenu[]"]',
    },
    classes: {
      grid: "custom-megamenu-grid",
      noPadding: "custom-megamenu-no-padding",
    },
    bannerColors: {
      red: "rgb(148, 70, 84)",
      blue: "rgb(62, 103, 121)",
      green: "rgb(74, 89, 74)",
      violet: "rgb(108, 88, 116)",
      primary: "rgb(25, 40, 56)",
    },
  };

  /**
   * Submenu configuration data
   * TODO: Consider moving this to Liquid data attributes for easier content management
   */
  const SUBMENU_DATA = [
    {
      submenu_1: {
        menu_max_width: "1440px",
        img: {
          enabled: true,
          url: "https://cdn.shopify.com/s/files/1/0973/5528/files/cool_top_menu_styles.jpg?v=1782928252",
          aspect_ratio: "1/1",
          alt: "Woman in green linen tee shirt",
          link: "/collections/linen-t-shirts-for-women",
          link_text: "Shop Linen Tee Shirts",
          col_start: "5",
          col_span: "2",
        },
        promo_banner: {
          enabled: true,
          text: "Our #1 Best-Selling Linen Pant",
          link: "/collections/floods-linen-pants-for-women",
          bg_color: "primary",
          text_color: "#ffffff",
        },
      },
    },
    {
      submenu_2: {
        menu_max_width: "1440px",
        img: {
          enabled: true,
          url: "https://cdn.shopify.com/s/files/1/0973/5528/files/socials_2026_menu_collections.jpg?v=1782928251",
          aspect_ratio: "1/1",
          alt: "Woman in a red linen dress and another woman in a white linen shirt and red linen pants",
          link: "/collections/socials-2026",
          link_text: "Shop the Socials Collection",
          col_start: "5",
          col_span: "2",
        },
        promo_banner: {
          enabled: true,
          text: "New Arrival: A Collection Rooted in Renewal",
          link: "/collections/salmon-sage-2026",
          bg_color: "primary",
          text_color: "#ffffff",
        },
      },
    },
    {
      submenu_3: {
        menu_max_width: "1440px",
        img: {
          enabled: true,
          url: "https://cdn.shopify.com/s/files/1/0973/5528/files/on-location-horizon-menu-2.jpg?v=1771533494",
          aspect_ratio: "1/1",
          alt: "FLAX women's linen clothing women celebrating in linen dresses",
          link: "/pages/looks/wedding-guest",
          link_text: "Shop Wedding Guest Looks",
          col_start: "5",
          col_span: "2",
        },
        promo_banner: {
          enabled: true,
          text: "Capsule Wardrobe: High-Quality Linen Staples",
          link: "/pages/looks/capsule-wardrobe",
          bg_color: "green",
          text_color: "#ffffff",
        },
      },
    },
    {
      submenu_4: {
        menu_max_width: "1440px",
        img: {
          enabled: true,
          url: "https://cdn.shopify.com/s/files/1/0973/5528/files/shirting_menu_outfits.jpg?v=1782928254",
          aspect_ratio: "1/1",
          alt: "Woman in a striped linen set",
          link: "/pages/looks/shirting-2026",
          link_text: "Shop Everyday Summer Styles",
          col_start: "5",
          col_span: "2",
        },
        promo_banner: {
          enabled: true,
          text: "NEW ARRIVALS: Designed for Real Life",
          link: "/pages/looks/urban-2026",
          bg_color: "primary",
          text_color: "#ffffff",
        },
      },
    },
    {
      submenu_5: {
        menu_max_width: "1440px",
        img: {
          enabled: true,
          url: "https://cdn.shopify.com/s/files/1/0973/5528/files/bold_affinity_menu_sale.jpg?v=1782928253",
          aspect_ratio: "1/1",
          alt: "Two women in FLAX pink and purple linen clothing",
          link: "/collections/bold-affinity-2025-womens-linen-clothing",
          link_text: "Shop 60% Off Collections",
          col_start: "5",
          col_span: "2",
        },
        promo_banner: {
          enabled: true,
          text: "BARN SALE MYSTERY PACKS: Something Unexpected",
          link: "/products/mystery-pack-linen-clothes-final-sale?variant=52549957026163",
          bg_color: "primary",
          text_color: "#ffffff",
        },
      },
    },
  ];

  /* ═══════════════════════════════════════════════════════════════════════════
     STATE
     ═══════════════════════════════════════════════════════════════════════════ */
  let wasDesktop = isDesktopBreakpoint();
  let resizeSyncRafId = null;
  let pendingToggleTrigger = null;

  /**
   * Recalculate header submenu height variables when a menu is currently open.
   * Keeps the underlay height synced after custom content injection and viewport changes.
   */
  const syncOpenSubmenuHeight = () => {
    if (!isDesktopBreakpoint()) return;

    const headerComponent = document.querySelector("#header-component");
    const headerMenu = document.querySelector("#header-component header-menu");

    if (
      !(headerComponent instanceof HTMLElement) ||
      !(headerMenu instanceof HTMLElement)
    ) {
      return;
    }

    const activeLink = headerMenu.querySelector(
      ".menu-list__link[aria-expanded='true']",
    );
    if (!(activeLink instanceof HTMLElement)) return;

    const activeItem = activeLink.closest(".menu-list__list-item");
    if (!(activeItem instanceof HTMLElement)) return;

    let submenuHeight = 0;

    const submenu = activeItem.querySelector('[ref="submenu[]"]');
    if (submenu instanceof HTMLElement) {
      submenuHeight = submenu.offsetHeight;
      if (submenuHeight === 0) {
        const submenuInner = submenu.querySelector(".menu-list__submenu-inner");
        if (submenuInner instanceof HTMLElement) {
          submenuHeight = submenuInner.offsetHeight;
        }
      }
    }

    // Overflow dropdown uses a shadow panel as the measurable container.
    if (submenuHeight === 0 && activeItem.getAttribute("slot") === "overflow") {
      const overflowList = headerMenu.querySelector("overflow-list");
      const overflowPanel =
        overflowList?.shadowRoot?.querySelector("[part='overflow']");
      if (overflowPanel instanceof HTMLElement) {
        submenuHeight = overflowPanel.offsetHeight;
      }
    }

    if (submenuHeight <= 0) return;

    const isOverlapSituation = headerComponent.hasAttribute(
      "data-submenu-overlap-bottom-row",
    );
    const topRow = headerComponent.querySelector(".header__row--top");
    const headerVisibleHeight =
      isOverlapSituation && headerComponent.offsetHeight > 0
        ? topRow instanceof HTMLElement
          ? topRow.offsetHeight
          : 0
        : headerComponent.offsetHeight;

    const fullOpenHeaderHeight = submenuHeight + headerVisibleHeight;

    headerComponent.style.setProperty("--submenu-height", `${submenuHeight}px`);
    headerComponent.style.setProperty(
      "--full-open-header-height",
      `${fullOpenHeaderHeight}px`,
    );
  };

  const scheduleSubmenuHeightSync = () => {
    if (!isDesktopBreakpoint()) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(syncOpenSubmenuHeight);
    });
  };

  const scheduleLiveResizeSync = () => {
    if (!isDesktopBreakpoint()) return;
    if (resizeSyncRafId !== null) return;

    resizeSyncRafId = requestAnimationFrame(() => {
      resizeSyncRafId = null;
      syncOpenSubmenuHeight();
    });
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     DOM OPERATIONS
     ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Inserts an image into a menu list
   * @param {Element} menuList - The menu list element
   * @param {object} imageData - Image configuration
   */
  const insertImage = (menuList, imageData) => {
    if (!isDesktopBreakpoint()) return;
    if (menuList.querySelector(CONFIG.selectors.image)) return;

    const imageHTML = `
      <li class="mega-menu__column mega-menu__column--span-${imageData.col_span} custom-megamenu-image-column"
          style="grid-column: ${imageData.col_start} / span ${imageData.col_span};">
        <div class="custom-megamenu-image">
          <a href="${imageData.link}" class="custom-megamenu-image-link">
            <div class="custom-megamenu-image-zoom">
              <img src="${imageData.url}"
                   alt="${imageData.alt}"
                   loading="eager"
                   style="aspect-ratio: ${imageData.aspect_ratio};"
                   class="custom-megamenu-image-img">
            </div>
            <span class="custom-megamenu-image-text">
              <span class="custom-megamenu-image-text-label">${imageData.link_text}</span>
              <svg class="custom-megamenu-image-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>
            </span>
          </a>
        </div>
      </li>
    `;

    menuList.insertAdjacentHTML("beforeend", imageHTML);
  };

  /**
   * Removes all mega menu images
   */
  const removeImages = () => {
    const imageColumns = document.querySelectorAll(
      CONFIG.selectors.imageColumn,
    );
    imageColumns.forEach((column) => column.remove());
  };

  /**
   * Inserts a promo banner into a submenu
   * @param {string} submenuId - The submenu ID
   * @param {object} bannerData - Banner configuration
   */
  const insertBanner = (submenuId, bannerData) => {
    const submenuContainer = document.getElementById(submenuId);
    if (!submenuContainer) return;

    if (submenuContainer.querySelector(CONFIG.selectors.banner)) return;

    const megaMenuSection = submenuContainer.querySelector(
      CONFIG.selectors.megaMenuSection,
    );
    if (!megaMenuSection) return;

    submenuContainer.classList.add(CONFIG.classes.noPadding);

    const backgroundColor =
      CONFIG.bannerColors[bannerData.bg_color] || bannerData.bg_color;

    const bannerHTML = `
      <div class="custom-megamenu-promo-banner"
           style="background-color: ${backgroundColor}; color: ${bannerData.text_color};">
        <a href="${bannerData.link}" class="custom-megamenu-promo-link">
          ${bannerData.text}
          <svg class="custom-megamenu-promo-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>
        </a>
      </div>
    `;

    megaMenuSection.insertAdjacentHTML("afterend", bannerHTML);
  };

  /**
   * Adds all mega menu enhancements (images and banners)
   */
  const addMegaMenuEnhancements = () => {
    for (let i = 0; i < SUBMENU_DATA.length; i++) {
      const submenuKey = `submenu_${i + 1}`;
      const submenu = SUBMENU_DATA[i]?.[submenuKey];

      if (!submenu) continue;

      const menuListId = `MegaMenuList-${i + 1}`;

      const menuGrid = document.querySelector(
        `[data-menu-grid-id="${menuListId}"]`,
      );
      const menuList = document.querySelector(
        `[data-menu-list-id="${menuListId}"]`,
      );

      if (!menuGrid || !menuList) continue;

      // Apply max-width to grid container
      if (
        submenu.menu_max_width &&
        !menuGrid.classList.contains(CONFIG.classes.grid)
      ) {
        menuGrid.classList.add(CONFIG.classes.grid);
        if (menuGrid instanceof HTMLElement) {
          menuGrid.style.maxWidth = submenu.menu_max_width;
        }
      }

      // Insert image if enabled
      if (submenu.img?.enabled) {
        insertImage(menuList, submenu.img);
      }

      // Insert promo banner if enabled
      if (submenu.promo_banner?.enabled) {
        const submenuId = `submenu-${i + 1}`;
        insertBanner(submenuId, submenu.promo_banner);
      }
    }

    scheduleSubmenuHeightSync();
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     EVENT HANDLERS
     ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Handles resize events to add/remove images at breakpoint
   */
  const handleResizeStructural = debounce(() => {
    const isNowDesktop = isDesktopBreakpoint();

    if (wasDesktop && !isNowDesktop) {
      // Crossed below breakpoint - remove images
      removeImages();
    } else if (!wasDesktop && isNowDesktop) {
      // Crossed above breakpoint - add images
      removeImages(); // Clear first to avoid duplicates
      addMegaMenuEnhancements();
    }

    wasDesktop = isNowDesktop;
  }, CONFIG.resizeDebounce);

  const handleResize = () => {
    scheduleLiveResizeSync();
    handleResizeStructural();
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     OBSERVERS
     ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Waits for header hydration before adding enhancements
   */
  const waitForHydration = () => {
    const headerSection = document.querySelector(
      CONFIG.selectors.headerSection,
    );
    if (!headerSection) return;

    if (
      headerSection instanceof HTMLElement &&
      headerSection.dataset.hydrated === "true"
    ) {
      addMegaMenuEnhancements();
      scheduleSubmenuHeightSync();
      return;
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type !== "attributes" ||
          mutation.attributeName !== "data-hydrated"
        )
          continue;

        if (
          headerSection instanceof HTMLElement &&
          headerSection.dataset.hydrated === "true"
        ) {
          observer.disconnect();
          addMegaMenuEnhancements();
          scheduleSubmenuHeightSync();
          break;
        }
      }
    });

    observer.observe(headerSection, {
      attributes: true,
      attributeFilter: ["data-hydrated"],
    });
  };

  /**
   * Handles cart update events by re-adding enhancements after morph
   * The cart drawer lives inside the header section, so when cart updates occur,
   * the header section gets morphed, wiping out our injected elements.
   */
  const handleCartUpdate = debounce(() => {
    if (!isDesktopBreakpoint()) return;
    addMegaMenuEnhancements();
    scheduleSubmenuHeightSync();
  }, 100);

  /**
   * Track whether a top-level trigger was already open before click handling starts.
   * This lets us close only on repeated clicks of the same open trigger.
   * @param {PointerEvent} event
   */
  const rememberTriggerStateOnPointerDown = (event) => {
    pendingToggleTrigger = null;
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(
      '.menu-list__link[aria-haspopup="true"]',
    );
    if (!(trigger instanceof HTMLElement)) return;

    const headerComponent = trigger.closest("#header-component");
    if (!(headerComponent instanceof HTMLElement)) return;
    if (headerComponent.getAttribute("data-menu-style") !== "menu") return;

    const isExpanded = trigger.getAttribute("aria-expanded") === "true";
    if (isExpanded) {
      pendingToggleTrigger = trigger;
    }
  };

  /**
   * Prevent native anchor navigation for parent mega-menu triggers that point to
   * same-page/hash URLs. This avoids unintended scroll-to-top on click-open.
   * @param {MouseEvent} event
   */
  const suppressTopLevelTriggerNavigation = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.defaultPrevented) return;
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const trigger = event.target.closest(
      '.menu-list__link[aria-haspopup="true"]',
    );
    if (!(trigger instanceof HTMLAnchorElement)) return;

    const headerComponent = trigger.closest("#header-component");
    if (!(headerComponent instanceof HTMLElement)) return;
    if (headerComponent.getAttribute("data-menu-style") !== "menu") return;

    const href = trigger.getAttribute("href");
    if (!href) return;

    if (href === "#" || href.startsWith("#")) {
      event.preventDefault();
      return;
    }

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }

    const isSamePage =
      url.origin === window.location.origin &&
      url.pathname === window.location.pathname &&
      url.search === window.location.search;

    if (isSamePage) {
      event.preventDefault();
    }
  };

  /**
   * Force a safe close path in click mode by temporarily disabling hover hit areas.
   * This prevents Horizon's hover guard from blocking deactivation on repeated trigger clicks.
   * @param {HTMLElement} headerMenu
   * @param {HTMLElement} trigger
   */
  const forceDeactivateHeaderMenu = (headerMenu, trigger) => {
    if (typeof headerMenu.deactivate !== "function") return;

    const overflowList = headerMenu.querySelector("overflow-list");
    const overflowPanel =
      overflowList?.shadowRoot?.querySelector("[part='overflow']");
    const overflowItems = overflowList?.shadowRoot?.querySelector(
      "[part='overflow-list']",
    );
    const hoverTargets = [overflowPanel, overflowItems].filter(
      (element) => element instanceof HTMLElement,
    );

    hoverTargets.forEach((element) => {
      element.dataset.customPrevPointerEvents = element.style.pointerEvents;
      element.style.pointerEvents = "none";
    });

    requestAnimationFrame(() => {
      headerMenu.deactivate({
        target: trigger,
        relatedTarget: document.body,
        type: "blur",
      });

      requestAnimationFrame(() => {
        hoverTargets.forEach((element) => {
          const previousPointerEvents = element.dataset.customPrevPointerEvents;
          if (previousPointerEvents) {
            element.style.pointerEvents = previousPointerEvents;
          } else {
            element.style.removeProperty("pointer-events");
          }
          delete element.dataset.customPrevPointerEvents;
        });
      });
    });
  };

  /**
   * Checks whether a focus target lives in the header menu, including shadow DOM.
   * @param {HTMLElement} headerMenu
   * @param {EventTarget | Node | null} target
   * @returns {boolean}
   */
  const isTargetInsideHeaderMenu = (headerMenu, target) => {
    if (!(target instanceof Node)) return false;
    if (headerMenu.contains(target)) return true;

    const root = target.getRootNode();
    return (
      root instanceof ShadowRoot &&
      root.host instanceof Node &&
      headerMenu.contains(root.host)
    );
  };

  /**
   * Gets the desktop header menu when Horizon is using the full menu layout.
   * @returns {HTMLElement | null}
   */
  const getDesktopHeaderMenu = () => {
    const headerComponent = document.querySelector(
      CONFIG.selectors.headerComponent,
    );
    if (!(headerComponent instanceof HTMLElement)) return null;
    if (headerComponent.getAttribute("data-menu-style") !== "menu") return null;

    const headerMenu = headerComponent.querySelector("header-menu");
    return headerMenu instanceof HTMLElement ? headerMenu : null;
  };

  /**
   * Clears stale expanded attributes and height variables after Horizon closes.
   * @param {HTMLElement} headerMenu
   */
  const resetHeaderMenuDomState = (headerMenu) => {
    const headerComponent = headerMenu.closest(
      CONFIG.selectors.headerComponent,
    );

    headerMenu
      .querySelectorAll(CONFIG.selectors.expandedMenuTrigger)
      .forEach((trigger) => {
        trigger.setAttribute("aria-expanded", "false");
      });

    headerMenu
      .querySelectorAll(`${CONFIG.selectors.submenu}[data-active]`)
      .forEach((submenu) => {
        delete submenu.dataset.active;
      });

    headerMenu.setAttribute("aria-expanded", "false");
    headerMenu.dataset.overflowExpanded = "false";
    headerMenu.style.setProperty("--submenu-opacity", "0");

    if (headerComponent instanceof HTMLElement) {
      headerComponent.style.setProperty("--submenu-height", "0px");
      headerComponent.style.setProperty("--full-open-header-height", "0px");
    }
  };

  /**
   * Finds the top-level trigger associated with a focused submenu item.
   * @param {EventTarget | Element | null} target
   * @returns {HTMLElement | null}
   */
  const getMenuTriggerFromTarget = (target) => {
    if (!(target instanceof Element)) return null;

    const listItem = target.closest(".menu-list__list-item");
    const trigger = listItem?.querySelector('[ref="menuitem"]');
    return trigger instanceof HTMLElement ? trigger : null;
  };

  /**
   * Closes the active mega menu through Horizon's public method, then cleans up
   * any stale expanded state left by focus transitions.
   * @param {HTMLElement} headerMenu
   * @param {HTMLElement | null} [trigger]
   */
  const closeHeaderMenu = (headerMenu, trigger = null) => {
    const activeTrigger =
      trigger || headerMenu.querySelector(CONFIG.selectors.expandedMenuTrigger);

    if (activeTrigger instanceof HTMLElement) {
      forceDeactivateHeaderMenu(headerMenu, activeTrigger);
    }

    requestAnimationFrame(() => {
      resetHeaderMenuDomState(headerMenu);
    });
  };

  /**
   * Close the mega menu once keyboard focus leaves the header menu entirely.
   * @param {FocusEvent} event
   */
  const handleHeaderMenuFocusOut = (event) => {
    const headerMenu = getDesktopHeaderMenu();
    if (!headerMenu) return;
    if (!isTargetInsideHeaderMenu(headerMenu, event.target)) return;

    const trigger = getMenuTriggerFromTarget(event.target);

    requestAnimationFrame(() => {
      if (
        isTargetInsideHeaderMenu(headerMenu, event.relatedTarget) ||
        isTargetInsideHeaderMenu(headerMenu, document.activeElement)
      ) {
        return;
      }

      closeHeaderMenu(headerMenu, trigger);
    });
  };

  /**
   * Backstop close for focus moves that originate outside delegated blur.
   * @param {FocusEvent} event
   */
  const handleDocumentFocusIn = (event) => {
    const headerMenu = getDesktopHeaderMenu();
    if (!headerMenu) return;
    if (isTargetInsideHeaderMenu(headerMenu, event.target)) return;

    closeHeaderMenu(headerMenu);
  };

  /**
   * Toggle open/close on repeated click of the same top-level trigger.
   * @param {MouseEvent} event
   */
  const handleHeaderMenuTriggerToggle = (event) => {
    if (!(event.target instanceof Element)) {
      pendingToggleTrigger = null;
      return;
    }

    const trigger = event.target.closest(
      '.menu-list__link[aria-haspopup="true"]',
    );
    if (!(trigger instanceof HTMLElement)) {
      pendingToggleTrigger = null;
      return;
    }

    const shouldClose = pendingToggleTrigger === trigger;
    pendingToggleTrigger = null;
    if (!shouldClose) return;

    const headerMenu = trigger.closest("header-menu");
    if (!(headerMenu instanceof HTMLElement)) return;

    event.preventDefault();
    forceDeactivateHeaderMenu(headerMenu, trigger);
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     MAIN EXECUTION
     ═══════════════════════════════════════════════════════════════════════════ */
  onDocumentReady(() => {
    waitForHydration();
    window.addEventListener("resize", handleResize);
    document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
    document.addEventListener("click", suppressTopLevelTriggerNavigation, true);
    document.addEventListener(
      "pointerdown",
      rememberTriggerStateOnPointerDown,
      true,
    );
    document.addEventListener("click", handleHeaderMenuTriggerToggle);
    document.addEventListener("focusout", handleHeaderMenuFocusOut, true);
    document.addEventListener("focusin", handleDocumentFocusIn, true);
  });
})();
